import type { EditorLine } from './line_rules';

/**
 * Video-guide timestamp tokens: `[videotimestamp|start|end]` (hosted video)
 * or `[youtubetimestamp|start|end]` (the legacy YouTube synonym). One token
 * per step, stored inline in the first line's wikitext — the token IS the
 * storage; there is no separate field on the step.
 *
 * Two deliberate behaviors: the editor fields stay visible when a step
 * starts at 0.0 seconds, and a token-less step on a video guide is a normal
 * state, not an error.
 */

export type StepTimestamps = {
   start: number;
   end: number;
};

const TOKEN_PATTERN = /\[(?:youtubetimestamp|videotimestamp)\|([^|\]]*)\|([^\]]*)\]/;

function zeroNegatives(value: string): number {
   const parsed = Number.parseFloat(value);
   return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * The step's timestamps from the first token found, or null when the step
 * has none. Negative values read as 0.
 */
export function parseStepTimestamps(lines: { text: string }[]): StepTimestamps | null {
   for (const line of lines) {
      const match = line.text.match(TOKEN_PATTERN);
      if (match) {
         return { start: zeroNegatives(match[1]), end: zeroNegatives(match[2]) };
      }
   }
   return null;
}

/**
 * Rewrites the lines so the step carries exactly the given timestamps:
 * the token lands on the first line (replacing one already there), every
 * other line's token is stripped, and null clears them all — never
 * leaving a duplicate token behind.
 */
export function applyStepTimestamps(
   lines: EditorLine[],
   timestamps: StepTimestamps | null,
   tokenType: 'youtubetimestamp' | 'videotimestamp'
): EditorLine[] {
   const stripped = lines.map(line => ({
      ...line,
      text: line.text.replace(new RegExp(TOKEN_PATTERN, 'g'), ''),
   }));

   if (timestamps === null || stripped.length === 0) {
      return stripped;
   }

   const token = `[${tokenType}|${timestamps.start}|${timestamps.end}]`;
   return stripped.map((line, index) =>
      index === 0 ? { ...line, text: `${token}${line.text}` } : line
   );
}
